import React, { useEffect, useState } from "react";
import api from "../api";

const TZ = "America/Winnipeg";

function formatDate(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: TZ,
  }).format(new Date(iso));
}

function StatusBadge({ status }) {
  const map = {
    PENDING: "pending",
    APPROVED: "approved",
    DECLINED: "declined",
    CANCELED: "canceled",
  };
  const cls = map[status] || "pending";
  return <span className={`booking-chip ${cls}`}>{status}</span>;
}

export default function BookingRequestsAdmin({ token, onAuthError }) {
  const [status, setStatus] = useState("PENDING");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [quoteDrafts, setQuoteDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    setError("");
    setActionMessage("");
    try {
      const data = await api(`/api/admin/booking-requests?status=${status}`, { token });
      setRequests(data.requests || []);
      setQuoteDrafts({});
    } catch (e) {
      if (e?.status === 401 && onAuthError) onAuthError();
      setError(e.message || "Failed to load booking requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, token]);

  const handleApprove = async (req) => {
    setBusyId(req.id);
    setError("");
    setActionMessage("");
    try {
      const draftQuote = quoteDrafts[req.id];
      const body = {};
      if (draftQuote !== undefined && draftQuote !== "") {
        body.quote_amount = Number(draftQuote);
      } else if (req.quote_amount) {
        body.quote_amount = Number(req.quote_amount);
      }
      const data = await api(`/api/admin/booking-requests/${req.id}/approve`, {
        method: "POST",
        body,
        token,
      });
      setActionMessage("Request approved and calendar updated.");
      setRequests((prev) => {
        const mapped = prev.map((r) => (r.id === req.id ? data.request : r));
        return status === "PENDING" ? mapped.filter((r) => r.status === "PENDING") : mapped;
      });
    } catch (e) {
      if (e?.status === 401 && onAuthError) onAuthError();
      setError(e.message || "Approval failed");
    } finally {
      setBusyId("");
    }
  };

  const handleDecline = async (req, admin_notes) => {
    setBusyId(req.id);
    setError("");
    setActionMessage("");
    try {
      const data = await api(`/api/admin/booking-requests/${req.id}/decline`, {
        method: "POST",
        body: { admin_notes },
        token,
      });
      setActionMessage("Request declined.");
      setRequests((prev) => {
        const mapped = prev.map((r) => (r.id === req.id ? data.request : r));
        return status === "PENDING" ? mapped.filter((r) => r.status === "PENDING") : mapped;
      });
    } catch (e) {
      if (e?.status === 401 && onAuthError) onAuthError();
      setError(e.message || "Decline failed");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="admin-card">
      <div className="booking-admin-header">
        <div>
          <h2 style={{ margin: 0 }}>Booking Requests</h2>
          <p className="text-muted" style={{ marginTop: 4 }}>
            Pending items show first. Times display in {TZ}.
          </p>
        </div>
        <div className="booking-filter">
          <label>Filter by status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="DECLINED">Declined</option>
            <option value="CANCELED">Canceled</option>
          </select>
        </div>
      </div>

      {loading ? <p className="text-muted">Loading requests...</p> : null}
      {error ? <p className="text-error">{error}</p> : null}
      {actionMessage ? <p className="text-success">{actionMessage}</p> : null}

      <div className="booking-requests-grid">
        {requests.map((req) => (
          <div key={req.id} className="booking-request-card">
            <div className="booking-card-head">
              <div>
                <p className="booking-card-space">{req.space}</p>
                <p className="text-muted">{formatDate(req.start_at)} — {formatDate(req.end_at)}</p>
              </div>
              <StatusBadge status={req.status} />
            </div>

            <div className="booking-card-body">
              <p><strong>Name:</strong> {req.name}</p>
              <p><strong>Email:</strong> {req.email}</p>
              <p><strong>Phone:</strong> {req.phone}</p>
              <p><strong>Description:</strong> {req.description}</p>
              <p>
                <strong>Quote:</strong>{" "}
                {req.quote_amount ? `${req.quote_amount} ${req.quote_currency || "CAD"}` : "TBD"}
              </p>
              <label className="booking-quote-edit">
                Update quote (optional)
                <input
                  type="number"
                  step="0.01"
                  value={
                    quoteDrafts[req.id] !== undefined
                      ? quoteDrafts[req.id]
                      : req.quote_amount || ""
                  }
                  onChange={(e) =>
                    setQuoteDrafts((prev) => ({
                      ...prev,
                      [req.id]: e.target.value,
                    }))
                  }
                  placeholder="Set amount before approval"
                />
              </label>
              <p className="text-muted">Created: {formatDate(req.created_at)}</p>
            </div>

            {req.status === "PENDING" ? (
              <div className="booking-card-actions">
                <button
                  type="button"
                  className="admin-btn-primary"
                  disabled={busyId === req.id}
                  onClick={() => handleApprove(req)}
                >
                  {busyId === req.id ? "Working..." : "Approve"}
                </button>
                <button
                  type="button"
                  className="admin-btn-secondary"
                  disabled={busyId === req.id}
                  onClick={() => {
                    const note = window.prompt("Add a short note (optional):", "");
                    if (note === null) return;
                    handleDecline(req, note || "");
                  }}
                >
                  Decline
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {!loading && !requests.length ? (
        <p className="text-muted">No booking requests found for this status.</p>
      ) : null}
    </div>
  );
}
